import { Row } from "@tanstack/react-table";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { handleFileClick, prepareDriveItem } from "../pages/drive/DriveUtils";

import { ONE, TEN, ZERO } from "@/constants/constants";
import { MoveOptions, MoveResult } from "@/hooks/useDriveMove";
import { PaginationMeta, ResponsePagination } from "@/hooks/usePagination";
import useQueryFilter from "@/hooks/useQueryFilter";
import { DirPath, IDrive } from "@/interfaces/project-register.interface";

// Define context types for common props between DriveTableView and DriveGridView
export interface DriveContextType {
  // Data
  data: IDrive[];

  // Folder navigation
  folderId: string | null;
  previousDir: string | null | undefined;
  setFolderId: (id: string) => void;
  resetFolderId: () => void;

  // Actions
  getActionOptions: (row: Row<IDrive>) => ActionButtonDataTypes[];

  // Drag and drop
  isDragging: boolean;
  isMoving: boolean;
  isCancelled: boolean;
  wasLastMoveCancelled: boolean;
  draggedItem: IDrive | null;
  activeFolderTarget: string | null;
  setActiveFolderTarget: (id: string | null) => void;
  handleDragStart: (item: IDrive) => void;
  handleDragEnd: () => void;
  handleDrop: (targetItem: IDrive) => void;
  handleCancelOperation: () => void;

  // Pagination
  pagination: ResponsePagination | undefined;
  setLocalPagination: React.Dispatch<React.SetStateAction<PaginationMeta>>;
  resetPagination: () => void;

  // Navigation helpers
  handleFileNavigation: (options: {
    url: string;
    isEmpty: boolean;
    folderId: string | null;
    previousDir: string | null | undefined;
  }) => void;

  // View type
  viewType: "table" | "grid";
  setViewType: (type: "table" | "grid") => void;

  type: string;
  ID: string;

  projectName?: string;
  clientName?: string;

  searchQuery: string;
  filterOperation: ReturnType<typeof useQueryFilter>;
  handleSearch: (value: string) => void;

  // directory path for pagination and path construction for batch upload
  directoryPath?: DirPath[];
}

// Create context with null initial value
const DriveContext = createContext<DriveContextType | null>(null);

// Context provider props
interface DriveProviderProps {
  children: React.ReactNode;
  initialData: {
    data: IDrive[];
    folderId: string | null;
    previousDir: string | null | undefined;
    set: (id: string) => void;
    reset: () => void;
    getActionOptions: (row: Row<IDrive>) => ActionButtonDataTypes[];
    pagination: ResponsePagination;
    setExternalLocalPagination: React.Dispatch<
      React.SetStateAction<PaginationMeta>
    >;
    viewType: "table" | "grid";
    setViewType: (type: "table" | "grid") => void;
    moveDriveItem?: (options: MoveOptions) => Promise<MoveResult>;
    cancelMove?: () => void; // Add cancelMove
    type: string;
    ID: string;
    projectName?: string;
    clientName?: string;
    directoryPath?: DirPath[];
  };
}

export const DriveProvider: React.FC<DriveProviderProps> = ({
  children,
  initialData,
}) => {
  // Extract initial data
  const {
    data,
    folderId,
    previousDir,
    set: originalSetFolderId,
    reset: originalResetFolderId,
    getActionOptions,
    pagination,
    setExternalLocalPagination,
    viewType: initialViewType,
    setViewType: originalSetViewType,
    moveDriveItem,
    cancelMove, // Extract cancelMove
    type,
    ID,
    projectName,
    clientName,
  } = initialData;

  // State management
  const [activeFolderTarget, setActiveFolderTarget] = useState<string | null>(
    null,
  );
  const [draggedItem, setDraggedItem] = useState<IDrive | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [wasLastMoveCancelled, setWasLastMoveCancelled] = useState(false);
  const [viewType, setViewTypeInternal] = useState(initialViewType);

  // Add search functionality
  const filterOperation = useQueryFilter("search");

  // Get current search query from URL
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const searchQuery = searchParams.get("search") || "";

  // Reset pagination helper
  const resetPagination = useCallback(() => {
    setExternalLocalPagination({
      limit: TEN,
      offset: ZERO,
      currentPage: ONE,
    });
  }, [setExternalLocalPagination]);

  // Handle search across views
  const handleSearch = useCallback(
    (value: string): void => {
      // Update the local pagination state directly instead of URL params
      setExternalLocalPagination((prev) => ({
        ...prev,
        currentPage: ONE,
        offset: ZERO,
        search: value,
      }));
    },
    [setExternalLocalPagination],
  );

  // Custom folder navigation with pagination reset
  const setFolderId = useCallback(
    (id: string) => {
      originalSetFolderId(id);
      resetPagination();
    },
    [originalSetFolderId, resetPagination],
  );

  const resetFolderId = useCallback(() => {
    originalResetFolderId();
    resetPagination();
  }, [originalResetFolderId, resetPagination]);

  // Handle file navigation
  const handleFileNavigation = useCallback(
    ({
      url,
      isEmpty,
      folderId,
      previousDir,
    }: {
      url: string;
      isEmpty: boolean;
      folderId: string | null;
      previousDir: string | null | undefined;
    }) => {
      handleFileClick({
        url,
        isEmpty,
        folderId,
        previousDir,
        set: setFolderId,
        reset: resetFolderId,
      });
      if (isEmpty) {
        resetPagination();
      }
    },
    [setFolderId, resetFolderId, resetPagination],
  );

  // Handle view type changes
  const setViewType = useCallback(
    (type: "table" | "grid") => {
      setViewTypeInternal(type);
      originalSetViewType(type);
    },
    [originalSetViewType],
  );

  // Drag and drop handlers
  const handleDragStart = useCallback((item: IDrive) => {
    // Don't allow dragging empty items (like the back navigation)
    if (item.empty) {
      return;
    }
    setWasLastMoveCancelled(false);
    setDraggedItem(item);
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    if (!isMoving) {
      setDraggedItem(null);
    }
  }, [isMoving]);

  // The handleDrop function implementation
  const handleDrop = useCallback(
    async (targetItem: IDrive) => {
      // Only allow drag and drop in grid view
      if (viewType !== "grid") {
        return;
      }

      // If we don't have a dragged item or valid target, cancel operation
      if (!draggedItem || !targetItem || draggedItem.id === targetItem.id) {
        setIsDragging(false);
        setDraggedItem(null);

        return;
      }

      // Can't drop on non-directory items (unless it's the back navigation)
      if (targetItem.type !== "dir" && !targetItem.empty) {
        setIsDragging(false);
        setDraggedItem(null);

        return;
      }

      // Reset cancellation flag and set moving state
      setWasLastMoveCancelled(false);
      setIsMoving(true);

      // Determine target directory - either the current folder or the back navigation
      let targetItemId: string;
      if (targetItem.empty && targetItem.name === "...") {
        targetItemId = previousDir || "ROOT";
      } else {
        targetItemId = targetItem.id;
      }

      // Setup source directory ID
      const sourceDirectoryId = folderId || "ROOT";

      // Prepare item for movement
      const item = prepareDriveItem({ original: draggedItem } as Row<IDrive>);

      try {
        if (moveDriveItem) {
          // Call the move function
          const result = await moveDriveItem({
            item,
            driveType: type,
            sourceItemId: ID,
            sourceDirectoryId,
            targetItemId,
          });

          // Handle cancellation
          if (!result.success && result.wasCancelled) {
            setWasLastMoveCancelled(true);
          }
        }
      } finally {
        // Reset state after a short delay
        setTimeout(() => {
          setIsDragging(false);
          setIsMoving(false);
          setDraggedItem(null);
        }, 1000);
      }
    },
    [draggedItem, folderId, ID, moveDriveItem, previousDir, type, viewType],
  );

  // Handle canceling the operation
  const handleCancelOperation = useCallback(() => {
    setIsCancelled(true);
    setWasLastMoveCancelled(true);

    // Use the provided cancelMove function if available
    if (cancelMove) {
      cancelMove();
    }

    // Reset states after a short delay
    setTimeout(() => {
      setIsMoving(false);
      setIsDragging(false);
      setDraggedItem(null);
      setIsCancelled(false);
    }, 500);
  }, [cancelMove]);

  // Keyboard listeners and cleanup
  useEffect(() => {
    // Clean up drag state when switching to table view
    if (viewType === "table" && (isDragging || draggedItem)) {
      setDraggedItem(null);
      setIsDragging(false);
    }

    // Add keyboard event listener for Escape key
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        if (isDragging || isMoving) {
          handleCancelOperation();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return (): void => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [viewType, isDragging, isMoving, draggedItem, handleCancelOperation]);

  // Create the context value
  const contextValue = useMemo(
    () => ({
      // Data
      data,

      // Folder navigation
      folderId,
      previousDir,
      setFolderId,
      resetFolderId,

      // Actions
      getActionOptions,

      // Drag and drop
      isDragging,
      isMoving,
      isCancelled,
      wasLastMoveCancelled,
      draggedItem,
      activeFolderTarget,
      setActiveFolderTarget,
      handleDragStart,
      handleDragEnd,
      handleDrop,
      handleCancelOperation,

      // Pagination
      pagination,
      setLocalPagination: setExternalLocalPagination,
      resetPagination,

      // Navigation helpers
      handleFileNavigation,

      // View type
      viewType,
      setViewType,

      // Additional state
      type,
      ID,

      //client and project names
      projectName,
      clientName,

      searchQuery,
      filterOperation,
      handleSearch,

      directoryPath: initialData.directoryPath,
    }),
    [
      data,
      folderId,
      previousDir,
      setFolderId,
      resetFolderId,
      getActionOptions,
      isDragging,
      isMoving,
      isCancelled,
      wasLastMoveCancelled,
      draggedItem,
      activeFolderTarget,
      setActiveFolderTarget,
      handleDragStart,
      handleDragEnd,
      handleDrop,
      handleCancelOperation,
      pagination,
      setExternalLocalPagination,
      resetPagination,
      handleFileNavigation,
      viewType,
      setViewType,
      type,
      ID,
      projectName,
      clientName,
      searchQuery,
      filterOperation,
      handleSearch,
      initialData.directoryPath,
    ],
  );

  return (
    <DriveContext.Provider value={contextValue}>
      {children}
    </DriveContext.Provider>
  );
};

// Custom hook to use the context
export const useDrive = (): DriveContextType => {
  const context = useContext(DriveContext);
  if (!context) {
    throw new Error("useDrive must be used within a DriveProvider");
  }

  return context;
};

export default DriveContext;
